import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SerializedStyles } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { AI_OFFICE_USER_UUID } from '@frontend/call-intel-core';
import { CalloutDialog } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { AiOfficeUser } from '@frontend/phone-ui';
import { useAppScopeStore } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { useDebouncedValue } from '@frontend/timer';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Text, usePopoverDialog } from '@frontend/design-system';
import { useOfficeUsers } from '../../hooks/use-office-users';
import { recentCallsTrackingId } from '../../pendo-tracking';
import {
  GroupedChecklistChipDropdownMenu,
  GroupedCheckListDropdownMenu,
  GroupOption,
} from './grouped-check-list-dropdown-menu';

const MAX_OFFICE_USER_SELECTION = 25;

/** The Office Staff group's own value, used to represent that group's selection without enumerating user ids. */
const OFFICE_USERS_GROUP_VALUE = 'office-users-group';

type HandledBySelectorFilters = {
  aiReceptionistHandled?: boolean;
  handledByUserIds?: string[];
  allOfficeUsers?: boolean;
};

type HandledBySelectorFilterProps = {
  filters: HandledBySelectorFilters;
  setFilters: (filters: HandledBySelectorFilters) => void;
  variant?: 'chip' | 'dropdown';
  showApplyButton?: boolean;
  /**
   * If false, we will never show the callout.
   */
  canShowCallout?: boolean;
  fieldContainerCss?: SerializedStyles;
  width?: number;
  trackingId?: string;
};

export const HandledBySelectorFilter = ({
  filters,
  setFilters,
  variant = 'dropdown',
  showApplyButton = true,
  canShowCallout = true,
  fieldContainerCss,
  width,
  trackingId,
}: HandledBySelectorFilterProps) => {
  const { t } = useTranslation('calls');
  const { selectedLocationIds } = useAppScopeStore();
  const { isActive: hasVoiceAi } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const [searchTerm, setSearchTerm] = useState('');
  const { debouncedValue: debouncedSearch } = useDebouncedValue(searchTerm, 300);

  const {
    activeOfficeUsers,
    labelMap,
    totalCount: totalHumanCount,
    isLoading: isOfficeUsersLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useOfficeUsers({
    locationIds: selectedLocationIds,
    search: debouncedSearch,
  });

  const [isOfficeUserCalloutDismissed, setOfficeUserCalloutDismissed] = usePreference(
    'CallHistoryHandledBySelectorCalloutDismissed'
  );

  const shouldShowCallout = useRef(
    !isOfficeUserCalloutDismissed && !(hasVoiceAi && filters.aiReceptionistHandled) && canShowCallout
  );

  const setCalloutDismissed = useCallback(
    (val: boolean) => {
      setOfficeUserCalloutDismissed(val);
      shouldShowCallout.current = false;
    },
    [setOfficeUserCalloutDismissed]
  );

  const {
    getDialogProps,
    close: closeCalloutDialog,
    open: openCalloutDialog,
    refs,
  } = usePopoverDialog({
    placement: 'right',
    interactionOptions: {
      dismiss: {
        outsidePress: false,
        escapeKey: false,
      },
    },
  });

  // Track the live (pre-Apply) selection of human users for count display and lock logic
  const [internalHumanUserSelection, setInternalHumanUserSelection] = useState<string[]>(
    () => filters.handledByUserIds ?? []
  );
  // Track whether the live selection includes the Office Staff group value, rather than individual ids
  const [isOfficeStaffGroupSelected, setIsOfficeStaffGroupSelected] = useState<boolean>(() => !!filters.allOfficeUsers);

  const handleStagedSelectionChange = useCallback(
    (selection: string[]) => {
      const isGroupSelected = selection.includes(OFFICE_USERS_GROUP_VALUE);
      const nextSelection = selection.filter((id) => id !== AI_OFFICE_USER_UUID && id !== OFFICE_USERS_GROUP_VALUE);

      setIsOfficeStaffGroupSelected(isGroupSelected);
      setInternalHumanUserSelection((previousSelection) =>
        isEqual(previousSelection, nextSelection) ? previousSelection : nextSelection
      );

      // Dismiss the callout as soon as the AI Receptionist option is clicked, without waiting for Apply
      if (hasVoiceAi && selection.includes(AI_OFFICE_USER_UUID) && shouldShowCallout.current) {
        setCalloutDismissed(true);
        closeCalloutDialog();
      }
    },
    [hasVoiceAi, setCalloutDismissed, closeCalloutDialog]
  );

  const selectedHumanCount = internalHumanUserSelection.length;

  // All office staff are selected when the group value is selected, OR (for rosters <= the selection
  // cap) every user has been checked individually. Uses the UNFILTERED total so an active search can't
  // make it look "all selected". Large rosters (> the cap) can only reach all-selected via the group value.
  const isOfficeStaffAllSelected =
    hasVoiceAi && (isOfficeStaffGroupSelected || (totalHumanCount > 0 && selectedHumanCount === totalHumanCount));

  // Show "(x/MAX_OFFICE_USER_SELECTION)" count only when the group is NOT all-selected and at least one user is selected
  const showCount = !isOfficeStaffAllSelected && selectedHumanCount > 0;

  const handledByValue = useMemo(() => {
    const selected: string[] = [];
    if (filters.aiReceptionistHandled) {
      selected.push(AI_OFFICE_USER_UUID);
    }
    // allOfficeUsers:true is a backend shorthand for "all users selected"; represent it with the
    // group's own value in selection (no per-user expansion, since the full roster may not be loaded).
    if (filters.allOfficeUsers) {
      selected.push(OFFICE_USERS_GROUP_VALUE);
    } else if (filters.handledByUserIds?.length) {
      selected.push(...filters.handledByUserIds);
    }
    return selected;
  }, [filters.aiReceptionistHandled, filters.handledByUserIds, filters.allOfficeUsers]);

  const onHandledByChange = useCallback(
    (selectedIds: string[]) => {
      const hasAiReceptionist = selectedIds.includes(AI_OFFICE_USER_UUID);
      const hasOfficeStaffGroupValue = selectedIds.includes(OFFICE_USERS_GROUP_VALUE);
      const userIds = selectedIds.filter((id) => id !== AI_OFFICE_USER_UUID && id !== OFFICE_USERS_GROUP_VALUE);
      // All office staff either via the explicit group value or by individually selecting the entire
      // roster (only reachable when the roster fits within the selection cap).
      const isAllOfficeStaff =
        hasVoiceAi && (hasOfficeStaffGroupValue || (totalHumanCount > 0 && userIds.length === totalHumanCount));
      setFilters({
        ...filters,
        aiReceptionistHandled: hasAiReceptionist ? true : undefined,
        handledByUserIds: isAllOfficeStaff ? undefined : userIds,
        allOfficeUsers: isAllOfficeStaff ? true : undefined,
      });
    },
    [filters, setFilters, hasVoiceAi, totalHumanCount]
  );

  const maxLimitTooltip = t(
    'Maximum {{count}} office staff selected. To select this user, deselect another user. To select all office staff, click "Office Staff."',
    { count: MAX_OFFICE_USER_SELECTION }
  );
  const allSelectedTooltip = t('To select individual Office Staff, deselect "Office Staff."');

  const groupedOfficeUsers: GroupOption[] = useMemo(() => {
    if (!hasVoiceAi) {
      return [
        {
          children: Object.entries(activeOfficeUsers).map(([id, label]) => {
            const isSelected = internalHumanUserSelection.includes(id);
            const isLockedByLimit = !isSelected && selectedHumanCount >= MAX_OFFICE_USER_SELECTION;
            return {
              label: label,
              value: id,
              locked: isLockedByLimit,
              tooltipText: isLockedByLimit ? maxLimitTooltip : undefined,
            };
          }),
          label: t('Office Users'),
          value: OFFICE_USERS_GROUP_VALUE,
          hideLabel: true,
        },
      ];
    }

    const officeStaffLabel = showCount ? (
      <Text weight='bold' as='span'>
        {t('Office Staff')} ({selectedHumanCount}/{MAX_OFFICE_USER_SELECTION})
      </Text>
    ) : (
      <Text weight='bold' as='span'>
        {t('Office Staff')}
      </Text>
    );

    const hasActiveSearch = debouncedSearch.length > 0;
    const lowerSearch = debouncedSearch.toLowerCase();
    const hasOfficeStaffChildren = Object.keys(activeOfficeUsers).length > 0;

    const aiReceptionistLabel = 'AI Receptionist';
    const showAiReceptionist = !hasActiveSearch || aiReceptionistLabel.toLowerCase().includes(lowerSearch);

    const result: GroupOption[] = [];

    if (showAiReceptionist) {
      result.push({
        children: [],
        label: (
          <AiOfficeUser
            fontSize={theme.font.size.large}
            fontWeight={theme.font.weight.bold}
            style={{ width: 'max-content' }}
          />
        ),
        labelText: t('AI Receptionist'),
        value: AI_OFFICE_USER_UUID,
        trackingId: 'ai-receptionist',
      });
    }

    if (hasOfficeStaffChildren) {
      result.push({
        children: Object.entries(activeOfficeUsers).map(([id, label]) => {
          const isSelected = internalHumanUserSelection.includes(id);
          const isLockedByAllSelected = isOfficeStaffAllSelected;
          const isLockedByLimit = !isSelected && selectedHumanCount >= MAX_OFFICE_USER_SELECTION;
          return {
            label: label,
            value: id,
            locked: isLockedByAllSelected || isLockedByLimit,
            tooltipText: isLockedByAllSelected ? allSelectedTooltip : isLockedByLimit ? maxLimitTooltip : undefined,
          };
        }),
        label: officeStaffLabel,
        labelText: t('Office Staff'),
        value: OFFICE_USERS_GROUP_VALUE,
        paginatedGroup: true,
        totalCount: totalHumanCount,
      });
    }

    return result;
  }, [
    activeOfficeUsers,
    debouncedSearch,
    hasVoiceAi,
    internalHumanUserSelection,
    isOfficeStaffAllSelected,
    selectedHumanCount,
    showCount,
    maxLimitTooltip,
    allSelectedTooltip,
    totalHumanCount,
    t,
  ]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (shouldShowCallout.current) {
      openCalloutDialog();
    }
  }, [openCalloutDialog]);

  const onDismissCallout = useCallback(() => {
    setCalloutDismissed(true);
    closeCalloutDialog();
  }, [closeCalloutDialog, setCalloutDismissed]);

  const calloutTitle = hasVoiceAi
    ? t('Filter Calls By AI Receptionist & Staff Members')
    : t('Filter Calls By Staff Members');
  const calloutDescription = hasVoiceAi
    ? t(
        'Filter your call history to see activity for specific office users, including calls managed by the AI Receptionist.'
      )
    : t('Filter your call history to see activity for specific office users.');

  if (variant === 'dropdown') {
    return (
      <div ref={refs.setReference}>
        <GroupedCheckListDropdownMenu
          disabled={isOfficeUsersLoading}
          fieldContainerCss={fieldContainerCss}
          groups={groupedOfficeUsers}
          label={t('Office User')}
          name='office-users'
          onChange={onHandledByChange}
          onStagedSelectionChange={handleStagedSelectionChange}
          placeholder={t('Select Office User')}
          renderIcon={() => <Icon name={'user-small'} color='default' />}
          showApplyButton={showApplyButton}
          value={handledByValue}
          width={width}
          showOverflowTooltip
          scrollableContentMaxHeight={200}
          trackingIdBase={trackingId}
          labelOverrides={labelMap}
          serverSideSearch
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onLoadMore={handleLoadMore}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
        />

        <CalloutDialog
          color='seaweed'
          popoverDialogProps={getDialogProps()}
          badge='new'
          title={
            <Text weight='bold' size='medium' as='span'>
              {calloutTitle}
            </Text>
          }
          cardStyles={{ width: '340px' }}
          containerStyles={{ overflow: 'visible' }}
          description={
            <Text weight='regular' size='medium' as='span'>
              {calloutDescription}
            </Text>
          }
          actions={
            <Button
              variant='tertiary'
              color='primary'
              onClick={onDismissCallout}
              trackingId={recentCallsTrackingId({ context: 'handled-by-callout::got-it' })}
            >
              {t('Got it')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <GroupedChecklistChipDropdownMenu
      groups={groupedOfficeUsers}
      label={t('Office User')}
      renderIcon={(isFilterActive) => <Icon name={'user-small'} color={isFilterActive ? 'white' : 'default'} />}
      onChange={onHandledByChange}
      onStagedSelectionChange={handleStagedSelectionChange}
      value={handledByValue}
      disabled={isOfficeUsersLoading}
      scrollableContentMaxHeight={200}
      serverSideSearch
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onLoadMore={handleLoadMore}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
    />
  );
};
